import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaGlobe, 
  FaClock, 
  FaUsers, 
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaBookOpen,
  FaCertificate,
  FaCalendarAlt,
  FaChartLine,
  FaMicroscope,
  FaDna,
  FaLaptopCode
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 200px;
  min-height: calc(100vh - 210px);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 50%, #0a2b3c 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    right: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: -50px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 217, 102, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 2rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.95;
  position: relative;
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;



const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  .value {
    font-size: 2.2rem;
    font-weight: 700;
    color: #0a4d68;
    margin-bottom: 0.3rem;
  }
  
  .label {
    color: #5a6b7a;
    font-size: 0.9rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ffd966;
    border-radius: 2px;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
  
  svg {
    font-size: 2rem;
    color: #ffd966;
    margin-bottom: 1rem;
  }
  
  h3 {
    color: #0a4d68;
    margin-bottom: 0.8rem;
  }
  
  p {
    color: #5a6b7a;
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ffd966;
    font-size: 1rem;
  }
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #0a4d68, #088395);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;
  
  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const CTAButton = styled(Link)`
  background: #ffd966;
  color: #0a4d68;
  border: none;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const ICD11 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  

  const features = [
    {
      icon: <FaGlobe />,
      title: "Global Standard",
      description: "ICD-11 is the global standard for health data, clinical documentation, and epidemiological analysis."
    },
    {
      icon: <FaMicroscope />,
      title: "Modern Structure",
      description: "Fully digital, with a new foundation component and improved clinical detail and accuracy."
    },
    {
      icon: <FaLaptopCode />,
      title: "Digital Ready",
      description: "Designed for integration with electronic health records and health information systems."
    }
  ];

  const improvements = [
    "Updated scientific content and clinical detail",
    "Better coding for primary care and digital documentation",
    "Enhanced functionality for quality and safety monitoring",
    "Improved interoperability with health IT systems",
    "New codes for antimicrobial resistance and patient safety",
    "Integration of traditional medicine conditions"
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          ICD-11 Coding
          <span>Next Generation Diagnosis Coding</span>
        </HeroTitle>
        <HeroDescription>
          Prepare for the future of diagnosis coding with comprehensive training on ICD-11, 
          the global standard for health data and clinical documentation.
        </HeroDescription>
      </HeroSection>

      <Container>
       

        <Grid2Col>
          <ContentBlock>
            <SectionTitle>Why ICD-11?</SectionTitle>
            <p>
              ICD-11 represents a fundamental shift in diagnosis coding, moving from the legacy 
              ICD-10 structure to a fully digital, scientifically updated classification system. 
              With over 55,000 unique codes and 1.6 million terms, ICD-11 provides unprecedented 
              clinical detail and accuracy.
            </p>
            <p>
              This comprehensive training program prepares you for the transition to ICD-11, 
              covering the new structure, coding conventions, and implementation strategies 
              for healthcare organizations worldwide.
            </p>
          </ContentBlock>
          
          <ImageWrapper>
            <img 
              src="https://images.unsplash.com/photo-1581093458791-9a3c3a2c3e4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
              alt="ICD-11 Coding"
            />
          </ImageWrapper>
        </Grid2Col>

        <FeatureGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              {feature.icon}
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </FeatureCard>
          ))}
        </FeatureGrid>

        <SectionTitle>Key Improvements in ICD-11</SectionTitle>
        <List>
          {improvements.map((item, index) => (
            <ListItem key={index}>
              <FaCheckCircle /> {item}
            </ListItem>
          ))}
        </List>

        <CTASection>
          <h3>Ready for the Future of Coding?</h3>
          <p>Join our ICD-11 training and stay ahead of the curve</p>
          <CTAButton to="/register">
            Enroll Now <FaArrowRight />
          </CTAButton>
        </CTASection>
      </Container>
    </PageContainer>
  );
};

export default ICD11;